// Admin -> Settings (SettingsPage + IntegrationsPanel), en — manba
export default {
  title: "Settings",
  subtitle: "Company-wide rules · every change is written to Audit log",
  ownerOnlyTitle: "Only the owner can change settings",
  ownerOnlyDesc: "Ask the owner to update statuses, assignment rules, KPI and security settings.",
  sections: {
    statuses: "Statuses & reasons",
    assignment: "Assignment",
    kpi: "KPI & bonus",
    expenses: "Budget",
    security: "Security",
    documents: "Documents",
    problems: "Driver rules",
    integrations: "Integrations",
  },
  problems: {
    title: "Problem drivers",
    formTitle: "Required when saving",
    formHint: "First name is always required. These fields must be filled in when a driver is created; existing records can still be edited.",
    lostReason: "Reason required when a driver is lost",
    lostReasonHint: "Rejected, not interested, quit.",
    receiptHint: "Whether an expense needs a receipt is set in",
    subtitle: "Which drivers count as needing attention. The same rules power the list filter, the row marker and the home page.",
    requiredTitle: "Required profile fields",
    requiredHint: "A driver missing any of these is marked \"profile incomplete\".",
    fromStatus: "Required from stage",
    fromStatusHint: "Earlier stages are not checked: a new lead may have only a name and a phone.",
    expiringDays: "Document expires within (days)",
    expiringHint: "CDL, medical card and uploaded documents.",
    staleHint: "\"No contact\" and \"stuck in stage\" use the {{days}}-day threshold set in",
    fields: { phone: "Phone", email: "Email", state: "State", city: "City", date_of_birth: "Date of birth", cdl_class: "CDL class", cdl_state: "CDL state", cdl_number: "CDL number", cdl_expires: "CDL expiry", medical_card_expires: "Medical card expiry", experience_months: "Experience", driver_type: "Driver type", freight_type: "Freight", endorsements: "Endorsements", equipment: "Equipment", home_time: "Home time", desired_pay: "Desired pay", available_from: "Available from" },
  },
  saved: "Settings saved",
  savedDesc: "Every change is written to the audit log.",
  conflict: "Settings were changed by someone else. Reloaded.",
  saveFailed: "Could not save settings.",
  unsaved_one: "{{count}} unsaved change",
  unsaved_other: "{{count}} unsaved changes",
  statuses: {
    title: "Statuses",
    subtitle: "The pipeline stages are fixed: New → Contacted → Qualified → Docs → Submitted → Background check → Orientation → Hired.",
    staleDays: "Stale card after (days)",
    staleDaysHint: "Pipeline cards turn red when a driver stays in one stage longer than this.",
    rejection: "Rejection reasons",
    rejectionDesc: "Required when a driver is marked Rejected (by a carrier).",
    lost: "Lost reasons",
    lostDesc: "Required for Not interested.",
    quit: "Quit reasons",
    quitDesc: "Required when a hired driver quits.",
    newReason: "New reason",
  },
  assignment: {
    title: "Driver ownership",
    period: "Ownership period",
    days: "days",
    periodHint: "A driver stays with the recruiter who added them first.",
    whenEnds: "When the period ends",
    extend: "Extend automatically if there was activity in the last {{days}} days",
    returnToPool: "Return to the branch pool",
    askManager: "Ask the branch manager to decide (notification)",
    window: "Activity window (days)",
    transferHint: "Manager can transfer a driver early (written to Audit log).",
    duplicates: "Duplicate check",
    phone: "Phone number",
    phoneHint: "compared in one format (+1 555 010 0142)",
    cdl: "CDL number + state",
    email: "Email",
    onMatch: "When a match is found",
    blockShowOwner: "Block creating and show the current owner",
    allowWarning: "Allow with a warning",
    blockRecruiters: "Block for recruiters, managers and the owner can add after confirming",
    always: "Phone number and CDL number — always: one number can belong to only one active driver (blocks regardless of the option below).",
    phoneSecondary: "Second phone",
    phoneSecondaryHint: "both numbers are compared with both numbers of other drivers",
    name: "First and last name",
    nameHint: "only warns: a different person can have the same name. Imported drivers have no phone — only this finds them",
    policyHint: "Applies to the second phone and email. With a warning, the user confirms \"this is a different person\" (written to the Audit log)."
  },
  kpi: {
    title: "Bonus rule",
    subtitle: "Bonuses are calculated from status history and approved in Budget → Bonuses.",
    ruleName: "Rule name",
    currency: "Currency",
    perHire: "Per approved hire",
    monthlyGoal: "Monthly hire goal per recruiter",
    monthlyGoalHint: "Shown as the goal ring widget; 0 turns the goal off",
    stayed30: "Stayed 30 days",
    stayed60: "Stayed 60 days",
    stayed90: "Stayed 90 days",
    approver: "Who approves bonuses",
    retention: "Retention checks (days)",
    retentionHint: "Comma-separated, e.g. 30, 60, 90",
    expiry: "Expiry reminders",
    expiryDesc: "Automatic reminders before CDL and medical card expiry.",
    daysBefore: "Days before expiry",
    daysBeforeHint: "Comma-separated, e.g. 30, 7",
  },
  expenses: {
    title: "Expense categories",
    subtitle: "Used in Budget → Expenses and Money → Expense. Commission % is optional: it is added when the expense is paid.",
    newCategory: "New category",
    rules: "Expense rules",
    receipt: "Receipt required",
    receiptHint: "An expense without a receipt cannot be submitted",
    approver: "Who approves expenses",
    alert: "Budget alert at (% of plan)",
    alertHint: "Owner is notified when a branch exceeds this",
    autoLimit: "No-approval limit",
    autoLimitHint: "A manager's expense up to this amount is approved automatically; 0 = approval is always required",
  },
  income: {
    title: "Income categories",
    subtitle: "Used in Money → Income (optional). Commission % is optional: it is deducted from the money received.",
    newCategory: "New category",
  },
  security: {
    twofa: "Two-factor authentication",
    twofaDesc: "People in these roles must set up an authenticator app before they can work, and cannot turn it off.",
    sessions: "Sessions and sign-in",
    sessionHours: "Session timeout (hours)",
    maxAttempts: "Failed attempts before lock",
    lockMinutes: "Lock duration (minutes)",
    applyNext: "Session and lock settings apply at the next sign-in.",
    public: "Public pages",
    publicDesc: "stage 3",
    applyForm: "Driver application form (/apply)",
    portalPhone: "Carrier portal shows driver phone",
    companyName: "Company name",
  },
  list: {
    rateHead: "Commission, %",
    rateAria: "Commission for {{item}}, %",
    remove: "Remove {{item}}",
    empty: "Empty list",
    add: "Add",
  },
  integrations: {
    pill: { error: "Error", connected: "Connected", disabled: "Disabled", notConfigured: "Not configured" },
    conflict: "Changed in another window. Reloaded.",
    saveFailed: "Could not save.",
    notConfigured: "Verifix is not configured",
    notConfiguredDesc: "Set VERIFIX_CLIENT_ID / VERIFIX_CLIENT_SECRET in .env and a filial_id first.",
    done: "{{kind}}: done",
    failed: "{{kind}} failed",
    requestFailed: "Request failed.",
    subtitle: "Filial: {{filial}} · last sync {{when}} · {{n}} employees",
    clientId: "Client ID",
    clientIdHint: "Client secret is set in .env on the server (never shown)",
    notSet: "— not set in .env",
    filialId: "Filial ID",
    filialHint: "Verifix organisation (filial_id header)",
    test: "Test connection",
    syncNow: "Sync now",
    pullSalaries: "Pull salaries",
    pushBonuses: "Push bonuses",
    enabled: "Enabled",
    lastError: "Last error",
    whatToSync: "What to sync",
    syncEmployees: "Employees & divisions → Users, Branches",
    hourly: "hourly",
    syncSalaries: "Salaries ← Verifix",
    monthlyPro: "monthly, Pro",
    pushBonusesLabel: "Approved bonuses → Verifix one-time charge",
    syncAttendance: "Attendance → Reports",
    daily: "daily",
    lastRun: "Last run",
    ok: "ok",
    runFailed: "failed",
    pullTitle: "Pull salaries from Verifix",
    pullDesc: "Only unpaid salaries are updated.",
    pull: "Pull",
    month: "Month",
    mappingSaved: "Mapping saved",
    mappingFailed: "Could not save mapping.",
    mapDivision: "Mapping: Verifix division → branch",
    mapEmployee: "Mapping: Verifix employee → user",
    mapSubtitle: "{{n}} active · {{unmatched}} unmatched",
    hideMatched: "Hide matched",
    showMatched: "Show matched",
    afterSync: "Mappings appear after the first sync.",
    nothingSynced: "Nothing synced yet. Press “Sync now”.",
    allMatched: "All matched.",
    colVerifix: "Verifix",
    colOurs: "Ours",
    colMapTo: "Map to",
    unmatched: "— Unmatched",
    none: "— none —",
    branchSuffix: "(branch)",
    errors: "Errors ({{n}})",
    errorsDesc: "every sync is written to Audit log",
  },
  google: {
    title: "Google account",
    subtitle: "Imports read Google Sheets as this account · read-only",
    pill: { connected: "Connected", reauth: "Reconnect needed", notConnected: "Not connected", notConfigured: "Not configured" },
    notConfiguredDesc: "Connecting a Google account needs a Google OAuth client. A developer creates it in Google Cloud and adds GOOGLE_OAUTH_CLIENT_ID and GOOGLE_OAUTH_CLIENT_SECRET to .env. Register this redirect URI in the OAuth client:",
    redirectUri: "Authorized redirect URI",
    setupDoc: "Step-by-step setup: docs/09-ishga-tushirish.md, section “Google akkaunt”.",
    whatFor: "Connect the Google account that owns the company sheets or has access to them. Imports then read sheets directly by link. The system never changes your sheets.",
    permission: { profile: "See the account's email and name", sheets: "Read Google Sheets (read-only)" },
    connect: "Connect Google account",
    connectFailed: "Could not start the Google connection.",
    connectedBy: "Connected by {{who}}",
    access: "Access",
    sheetsReadOnly: "Google Sheets, read-only",
    noSheetsAccess: "No access to Google Sheets — reconnect and allow it",
    lastUsed: "Last used",
    neverUsed: "Not used yet",
    switchAccount: "Use another account",
    reconnect: "Reconnect",
    disconnect: "Disconnect",
    disconnected: "Google account disconnected",
    disconnectFailed: "Could not disconnect.",
    disconnectTitle: "Disconnect Google account?",
    disconnectDesc: "The system will lose access to the Google Sheets of {{email}}. Imports by link stop working until a Google account is connected again. Already imported data stays.",
    reauthTitle: "Google access has expired",
    reauthDesc: "Google rejected the saved access (the password was changed or access was removed). Reconnect the account to read sheets again.",
    technical: "Technical details",
    clientId: "Client ID",
    copy: "Copy",
    copied: "Copied",
    copyFailed: "Could not copy. Select the text and copy it manually.",
    result: {
      connected: "Google account connected",
      connectedDesc: "Imports can now read Google Sheets by link.",
      cancelled: "Google connection was cancelled.",
      failed: "Google account was not connected",
    },
    reason: {
      state: "The connection request expired or did not match. Start again from this page.",
      scope: "Access to Google Sheets was not allowed. Connect again and keep the Google Sheets box checked.",
      forbidden: "Only the owner can connect a Google account.",
      denied: "Google denied access.",
      redirect_uri_mismatch: "The redirect URI is not registered in the Google OAuth client. Add the URI from “Technical details” below in Google Cloud.",
      invalid_client: "Google rejected the client ID or secret. Check GOOGLE_OAUTH_CLIENT_ID and GOOGLE_OAUTH_CLIENT_SECRET in .env.",
      invalid_grant: "The Google code has expired. Try connecting again.",
      no_refresh_token: "Google did not give offline access. Remove the app in your Google account (Security → Third-party access) and connect again.",
      network: "Could not reach Google. Check the internet connection and try again.",
      userinfo: "Could not read the Google account email. Try again.",
      unknown: "Error code: {{code}}",
    },
  },
  payments: {
    title: "Carrier payments",
    subtitle: "Money a carrier pays the company for each hired driver.",
    dueDays: "Due after the driver starts (days)",
    dueDaysHint: "Used for new hires; already hired drivers keep their date",
  },
  documents: {
    title: "Document types",
    subtitle: "What recruiters collect from a driver. Required types count toward “Submitted”; expiring types show a validity date.",
    name: "Name",
    key: "Key",
    required: "Required",
    expires: "Has expiry date",
    remove: "Remove {{name}}",
    protectedHint: "CDL and medical card stay: AI reading and expiry reminders are tied to them.",
    newType: "New document type",
    add: "Add",
    blockTitle: "Submitting to a carrier",
    block: "Block “Submitted” until all required documents are uploaded",
    blockHint: "Off: the status dialog only warns about missing documents.",
    ocrNote: "AI reading (Gemini) is set up in Integrations.",
  },
  ocr: {
    title: "AI document reading",
    subtitle: "Gemini reads CDL and medical card photos and suggests profile fields. Nothing changes until a person accepts.",
    pill: { on: "On", off: "Off", notConfigured: "Not configured" },
    notConfigured: "Not connected yet. On the server .env: either Vertex AI with Application Default Credentials (GEMINI_USE_VERTEX=1, GOOGLE_CLOUD_PROJECT) or an AI Studio paid-tier key (GEMINI_API_KEY) — docs/09-ishga-tushirish.md.",
    model: "Model",
    auth: "Connection",
    authVertex: "Vertex AI · project {{project}} (Application Default Credentials)",
    authKey: "AI Studio API key",
    enabled: "Read uploaded documents automatically",
    kinds: "Document types to read",
    month: "This month",
    monthLine: "{{documents}} read · {{failed}} failed · {{input}} in / {{output}} out tokens",
    cost: "≈ ${{cost}}",
    costUnknown: "price for this model is not in the table",
    privacy: "Paid tier only: Google does not train on paid requests. SSN is never read; the application form is not sent.",
    saved: "AI reading settings saved",
  },
  sheets: {
    title: "Google Sheets copy",
    subtitle: "The current state in the layout of the old spreadsheet: Partners, a Database tab per branch and Pipeline",
    pill: { on: "On", paused: "Paused", error: "Error", off: "Not connected" },
    download: "Download (.xlsx)",
    notConfiguredDesc: "An automatic copy needs a Google service account. A developer sets it up once: docs/09-ishga-tushirish.md, section “Google Sheets nusxasi”. Until then, download the file — it opens in Excel or in Google Drive.",
    step1: "Create an empty spreadsheet in Google Sheets.",
    step2: "Share it (Share button) with this email as Editor:",
    step3: "Paste the spreadsheet link:",
    link: "Spreadsheet link",
    connect: "Connect",
    firstWrite: "Writing the spreadsheet for the first time…",
    connected: "Spreadsheet connected and filled",
    untitled: "Spreadsheet",
    updated: "Updated {{when}}",
    notYet: "Not written yet",
    connectedBy: "connected by {{who}}",
    syncNow: "Update now",
    synced: "Spreadsheet updated",
    errorTitle: "The last update failed",
    auto: "Update automatically",
    autoHint: "within 2 minutes after a change",
    rows_one: "{{count}} row",
    rows_other: "{{count}} rows",
    disconnect: "Disconnect",
    disconnectTitle: "Disconnect the spreadsheet?",
    disconnectDesc: "The platform stops updating it. The spreadsheet itself and its data stay in Google Drive.",
    disconnected: "Spreadsheet disconnected",
    oneWay: "One-way copy: edits made in the spreadsheet are overwritten on the next update. Everyone with access to the spreadsheet sees all drivers — share it carefully.",
  },
};
